/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Button, Paper } from '@mui/material';
import { Brightness1, Warning } from '@mui/icons-material';
import Terminal, { TerminalDetails } from '../../../shared/entities/terminal';
import StyledProps from '../../common/theme/AppThemeInterface';
import { connect, shallowEqual, useSelector } from 'react-redux';

import { RootState, selectRolesForCurrentUser, selectTerminalDetails } from '../../../store';
import { RouteComponentProps, withRouter } from 'react-router';
import * as UiActions from '../../../store/ui/actions';
import * as TerminalActions from '../../../store/terminals/actions';
import * as MapActions from '../../../store/maps/actions';
import { DetailsPanelGroupCollapseState } from '../../../store/ui/reducer';
import CircularProgress from '@mui/material/CircularProgress';

import { isOnline } from '../../../utils/ConnectionStatusUtils';
import isNil from 'lodash/isNil';
import Config from '../../../shared/entities/config';
import TerminalTabContainer from './TabbedPanel/TerminalTabContainer';
import { OFFLINE_BANNER_HEIGHT_PX } from './TabbedPanel/TerminalTabPanel';
import { useEffect, useCallback } from 'react';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from '../FleetMap';
import TailDetailsPanelHeader from './TailDetailsPanelHeader';
import { DetailsPanelEnum } from '../../../shared/entities/detailsPanel';
import { isPathnameFullSpaceVehicleView } from '../../../utils/NavigatorUtils';
import SpaceVehicleDetailsPanelFullSpaceVehicleHeader from './SpaceVehicleDetailsPanelFullSpaceVehicleHeader';
import { PortalsEnum } from '../../../shared/entities/portal';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../common/map/GoogleMapSingleton';

export const TAIL_DETAILS_PANEL_WIDTH = 360;
export const TAIL_DETAILS_TAIL_NUMBER_TOOLTIP_WIDTH_PX = 232;
export const PANEL_HEADER_HEIGHT_PX = 57;
const FLEET_VIEW_PANEL_WIDTH_ADJUSTMENT_PX = 2;

interface PanelProps {
  isFullTailViewMode: boolean;
  terminalListDrawerOpen: boolean;
  rolesForCurrentUser: string[];
  mapRulerToolShown: boolean;
}

const Panel = styled(Paper)`
  ${({ isFullTailViewMode, terminalListDrawerOpen }: PanelProps) => css`
    margin-left: ${isFullTailViewMode ? '0px' : terminalListDrawerOpen ? `${KU_PLUS_KA_DRAWER_WIDTH_PX}px` : '32px'};
    position: ${isFullTailViewMode ? 'fixed' : 'absolute'};
    width: ${TAIL_DETAILS_PANEL_WIDTH - (!isFullTailViewMode ? FLEET_VIEW_PANEL_WIDTH_ADJUSTMENT_PX : 0)}px;
  `};
  height: calc(100vh - 104px);
  position: ${(props: any) => props.position};
  z-index: 2;
  min-height: 600px;
  overflow: hidden;
  margin-left: ${(props: any) => props.marginLeft};
  color: white;
  border-left: 0.5px solid ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  border-right: 0.5px solid ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  overscroll-behavior: none;
  box-sizing: content-box;
  .empty_button_spacer {
    height: 25px;
  }
  .loading__error__message__container {
    position: absolute;
    width: 100%;
    top: ${({ tailDetailsPanelTerminal }: TailDetailsPanelProps) => {
      return isNil(tailDetailsPanelTerminal) || !isOnline(tailDetailsPanelTerminal.connectivityStatus)
        ? 50 + OFFLINE_BANNER_HEIGHT_PX
        : 50;
    }}px;
    height: 100px;
    text-align: center;
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};
  }
  .progress {
    margin-top: 10px;
  }
  .tail__details__scrollbar__wrapper {
    position: relative;
    width: 100%;
  }
  .tail__details_faded_wrapper {
    opacity: 0.6;
  }
  .tail__details__panel__header__collapse__button {
    right: 10px;
    top: 6px;
    color: white;
    position: absolute;
    .tail__details__panel__header__collapse__icon {
      height: 20px;
      width: 20px;
      color: white;
    }
  }
  .tail__details__panel____header__pan__to__terminal__button {
    top: 6px;
    right: 45px;
    color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
    position: absolute;
    .tail__details__panel____header__pan__to__terminal__icon {
      color: ${({ theme }: StyledProps) => theme.primaryTextColor};
      margin-top: 1px;
      height: 18px;
      width: 18px;
    }
  }

  .tail__details__panel____header__ruler__tool__button {
    top: 6px;
    right: 82px;
    color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
    position: absolute;
    .tail__details__panel____header__ruler__tool__icon {
      color: ${(props: any) =>
        props.mapRulerToolShown ? props.theme.buttonSelectedColor : props.theme.primaryTextColor};
      margin-top: 1px;
      height: 18px;
      width: 18px;
    }
  }

  .Mui-disabled .tail__details__panel____header__pan__to__terminal__icon {
    color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
  }

  .Mui-disabled .tail__details__panel____header__ruler__tool__icon {
    color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
  }

  .tail__details__panel____header__restricted__icon__container {
    top: 18px;
    right: 55px;
    color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
    position: absolute;
    .tail__details__panel____header__restricted__icon {
      color: ${({ theme }: StyledProps) => theme.primaryTextColor};
      margin-top: 1px;
      height: 18px;
      width: 18px;
    }
  }
  .offline__values__banner {
    background-color: ${({ theme }: StyledProps) => theme.offlineStatusColor};
    height: ${OFFLINE_BANNER_HEIGHT_PX}px;
    color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
    width: ${TAIL_DETAILS_PANEL_WIDTH}px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    .offline__values__banner__icon {
      height: 18px;
      width: 18px;
      margin-left: 18px;
      margin-right: 7px;
      color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
      position: relative;
    }
    .offline__values__banner__source__time__tooltip {
      position: absolute;
      background-color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
      z-index: 5;
      .arrow {
        position: relative;
        color: ${({ theme }: StyledProps) => theme.tooltipBackgroundColor};
      }
      color: white;
    }
    .offline__values__text {
      font-size: 16px;
      font-weight: normal;
      height: 20px;
      letter-spacing: 0px;
      line-height: 20px;
    }
    z-index: 6;
  }
`;

export const ActiveIndicator = styled(Brightness1)`
  height: 14px;
  width: 14px;
  margin-bottom: 2px;
  position: relative;
  color: ${({ theme }: StyledProps) => theme.activeStatusColor};
  display: inline-block;
`;

export const StandbyIndicator = styled(Brightness1)`
  height: 14px;
  width: 14px;
  margin-bottom: 2px;
  position: relative;
  color: ${({ theme }: StyledProps) => theme.standbyStatusColor};
  display: inline-block;
`;

export const OfflineIndicator = styled(Brightness1)`
  height: 14px;
  width: 14px;
  margin-bottom: 2px;
  position: relative;
  color: ${({ theme }: StyledProps) => theme.offlineStatusColor};
  display: inline-block;
`;

export interface TailDetailsPanelStateProps {
  detailsPanelVariant: DetailsPanelEnum;
  terminalListDrawerOpen: boolean;
  detailsPanelOpen: boolean;
  isRequestingTailDetailsPanelTerminal: boolean;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
  selectedDatapathUuidFleetView: string | undefined;
  selectedTerminalTabIndex: number | undefined;
  requestTailDetailsPanelTerminalError: string | undefined;
  terminals: { [key: string]: Terminal };
  isFullTailViewMode: boolean;
  isNextSatellitePanelEnabled: boolean;
  isSatellitesInFOVEnabled: boolean;
  appConfig: Config;
  hiddenTerminals: string[];
  pathname: any;
  mapRulerToolShown: boolean;
  mapRulerToolFollowAircraft: boolean;
}

export interface TailDetailsPanelDispatchProps {
  openDetailsPanel: Function;
  closeDetailsPanel: Function;
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
  getTailDetailsPanelTerminal: (datapathUuid: string) => {};
  clearTailDetailsPanelTerminal: Function;
  cancelRequestTailDetailsPanelTerminal: Function;
  clearFlightPath: Function;
  setMapZoomLatLngFleetView: Function;
  toggleMapRulerTool: Function;
  hideMapRulerTool: Function;
}

export interface TailDetailsPanelProps
  extends TailDetailsPanelStateProps,
    TailDetailsPanelDispatchProps,
    RouteComponentProps {}

function TailDetailsPanel(props: TailDetailsPanelProps) {
  const {
    detailsPanelVariant,
    getTailDetailsPanelTerminal,
    selectedDatapathUuidFleetView,
    cancelRequestTailDetailsPanelTerminal,
    tailDetailsPanelTerminal,
    history,
    detailsPanelGroupCollapseState,
    setDetailsPanelGroupCollapseState,
    appConfig,
    terminalListDrawerOpen,
    isFullTailViewMode,
    isRequestingTailDetailsPanelTerminal,
    requestTailDetailsPanelTerminalError,
    isSatellitesInFOVEnabled,
    isNextSatellitePanelEnabled,
    clearTailDetailsPanelTerminal,
    pathname,
    mapRulerToolShown,
  } = props;
  const getTerminalData = useCallback(() => {
    if (selectedDatapathUuidFleetView) {
      getTailDetailsPanelTerminal(selectedDatapathUuidFleetView);
    }
  }, [selectedDatapathUuidFleetView, getTailDetailsPanelTerminal]);

  const rolesForCurrentUser = useSelector(selectRolesForCurrentUser, shallowEqual);
  const isFullSpaceVehicleView = isPathnameFullSpaceVehicleView(pathname.substr(1, pathname.length));

  // Effect 1: Fetch data when selection changes (no cleanup of terminal state)
  useEffect(() => {
    getTerminalData();
  }, [getTerminalData]);

  // Effect 2: Cleanup only on component unmount
  useEffect(() => {
    return () => {
      cancelRequestTailDetailsPanelTerminal();
      clearTailDetailsPanelTerminal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only runs on mount/unmount

  const tailDetailsPanelHeader = useMemo(() => {
    if (!isFullTailViewMode) {
      return <TailDetailsPanelHeader {...props} />;
    } else if (isFullTailViewMode && isFullSpaceVehicleView) {
      return <SpaceVehicleDetailsPanelFullSpaceVehicleHeader {...props} />;
    } else {
      return null;
    }
  }, [isFullTailViewMode, isFullSpaceVehicleView, props]);

  return (
    <Panel
      terminalListDrawerOpen={terminalListDrawerOpen}
      isFullTailViewMode={isFullTailViewMode}
      rolesForCurrentUser={rolesForCurrentUser}
      mapRulerToolShown={mapRulerToolShown}
    >
      <React.Fragment>{tailDetailsPanelHeader}</React.Fragment>
      <React.Fragment>
        {requestTailDetailsPanelTerminalError === undefined &&
        !isRequestingTailDetailsPanelTerminal &&
        !isNil(tailDetailsPanelTerminal) ? (
          <TerminalTabContainer
            tailDetailsPanelTerminal={tailDetailsPanelTerminal}
            isSatellitesInFOVEnabled={isSatellitesInFOVEnabled}
            isNextSatellitePanelEnabled={isNextSatellitePanelEnabled}
            isFullTailViewMode={isFullTailViewMode}
            appConfig={appConfig}
            detailsPanelGroupCollapseState={detailsPanelGroupCollapseState}
            setDetailsPanelGroupCollapseState={setDetailsPanelGroupCollapseState}
            history={history}
            rolesForCurrentUser={rolesForCurrentUser}
            detailsPanelVariant={detailsPanelVariant}
          />
        ) : null}
      </React.Fragment>

      {requestTailDetailsPanelTerminalError ? (
        <div className="loading__error__container">
          <div className="loading__error__message__container">
            <Warning />
            <div>Error Retrieving Tail Details</div>
            <Button variant="text" color="primary" sx={{ zIndex: 5 }} onClick={getTerminalData}>
              RETRY
            </Button>
          </div>
        </div>
      ) : null}

      {isRequestingTailDetailsPanelTerminal || isNil(tailDetailsPanelTerminal) ? (
        <div className="loading__error__container">
          <div className="loading__error__message__container">
            <CircularProgress className="progress" />
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

export default connect(
  (state: RootState, ownProps: any): TailDetailsPanelStateProps => ({
    detailsPanelVariant:
      ownProps.detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE
        ? DetailsPanelEnum.SPACE_VEHICLE // TODO: Add Network logic when ISAN Network is implemented in Redux
        : DetailsPanelEnum.TAIL,
    tailDetailsPanelTerminal: selectTerminalDetails(state),
    isRequestingTailDetailsPanelTerminal: state.terminals.isRequestingTailDetailsPanelTerminal,
    requestTailDetailsPanelTerminalError: state.terminals.requestTailDetailsPanelTerminalError,
    terminalListDrawerOpen: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListDrawerOpen,
    detailsPanelOpen: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].detailsPanelOpen,
    detailsPanelGroupCollapseState:
      state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].detailsPanelGroupCollapseState,
    selectedDatapathUuidFleetView:
      state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList,
    selectedTerminalTabIndex: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
    terminals: state.terminals.terminals,
    isFullTailViewMode: ownProps.isFullTailViewMode,
    isSatellitesInFOVEnabled: state.config.appConfig.isSatellitesInFOVEnabled,
    isNextSatellitePanelEnabled: state.config.appConfig.isNextSatellitePanelEnabled,
    appConfig: state.config.appConfig,
    hiddenTerminals: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList,
    pathname: state.router.location.pathname,
    mapRulerToolShown: state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.mapRulerToolShown,
    mapRulerToolFollowAircraft: state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.mapRulerToolFollowAircraft,
  }),
  (dispatch: any, ownProps: any): TailDetailsPanelDispatchProps => ({
    openDetailsPanel: () => {
      return dispatch(UiActions.openDetailsPanel());
    },
    closeDetailsPanel: () => {
      return dispatch(UiActions.closeDetailsPanel());
    },
    setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {
      return dispatch(UiActions.setDetailsPanelGroupCollapseState(detailsPanelGroupCollapseState));
    },
    getTailDetailsPanelTerminal: (datapathUuid: string) => {
      if (ownProps.detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE) {
        return dispatch(TerminalActions.getTailDetailsPanelTerminal(datapathUuid, PortalsEnum.ISAN_PORTAL));
      }
      return dispatch(TerminalActions.getTailDetailsPanelTerminal(datapathUuid));
    },
    clearTailDetailsPanelTerminal: () => {
      return dispatch(TerminalActions.clearDetailsPanelTerminal());
    },
    clearFlightPath: () => {
      if (ownProps.detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE) {
        return dispatch(TerminalActions.clearFlightPath(PortalsEnum.ISAN_PORTAL));
      }
      return dispatch(TerminalActions.clearFlightPath());
    },
    cancelRequestTailDetailsPanelTerminal: () => {
      return dispatch(TerminalActions.cancelRequestTailDetailsPanelTerminal());
    },
    setMapZoomLatLngFleetView: (latLng: google.maps.LatLng) => {
      if (ownProps.detailsPanelVariant === DetailsPanelEnum.SPACE_VEHICLE && !ownProps.isFullTailViewMode) {
        return dispatch(MapActions.setMapZoomLatLng(MapActions.TRANSPORT_MONITORING_MAP_ID)(latLng));
      }
      return dispatch(MapActions.setMapZoomLatLng(MapActions.FLEET_MAP_ID)(latLng));
    },
    toggleMapRulerTool: () => {
      return dispatch(MapActions.toggleMapRulerTool(GOOGLE_MAP_INSTANCE_KEY)(true));
    },
    hideMapRulerTool: () => {
      return dispatch(MapActions.hideMapRulerTool(GOOGLE_MAP_INSTANCE_KEY)());
    },
  }),
)(withRouter(TailDetailsPanel));
